/**
 * Il pop-up del quaderno: una scheda per volta, in una finestra.
 *
 * Prima stava sopra la chat, a conversazione vuota. Adesso si apre dalla
 * campanella: quando il giro settimanale prepara schede nuove arriva «Ho 2
 * risposte da farti controllare», e il tasto apre questa finestra. Così non
 * salta mai addosso a metà di una frase — lo apre l'host, quando vuole.
 *
 * Una sola scheda alla volta, e nessun contatore tipo «1 di 12»: davanti a
 * dodici cose da decidere si rimanda; davanti a una si decide.
 *
 * «Non ora» non è un no e non scrive niente: la scheda resta proposta e si
 * ritrova nella campanella. Il no vero si dà in Impostazioni, dove c'è «Non
 * la uso».
 */

import { useEffect, useState } from "react";
import { NotebookPen } from "lucide-react";
import { api, daControllare, nomeCasa, type SchedaView } from "../lib/api.ts";
import { Modal } from "../ui/Modal.tsx";
import { Pill } from "../ui/primitives.tsx";

export function SchedaPopup({
  aperta,
  onChiudi,
}: {
  aperta: boolean;
  onChiudi: () => void;
}) {
  const [coda, setCoda] = useState<SchedaView[] | null>(null);
  const [inCorrezione, setInCorrezione] = useState(false);
  const [bozza, setBozza] = useState("");

  useEffect(() => {
    if (!aperta) return;
    let vivo = true;
    setCoda(null);
    setInCorrezione(false);
    void api
      .quaderno()
      .then((r) => {
        if (vivo) setCoda(daControllare(r.schede));
      })
      .catch(() => {
        if (vivo) setCoda([]);
      });
    return () => {
      vivo = false;
    };
  }, [aperta]);

  // Finita la coda si chiude da sola: una finestra vuota non serve a niente.
  useEffect(() => {
    if (aperta && coda && coda.length === 0) onChiudi();
  }, [aperta, coda, onChiudi]);

  const scheda = coda?.[0];

  const avanti = () => {
    setInCorrezione(false);
    setCoda((prima) => (prima ?? []).slice(1));
  };

  const decidi = (stato: "attiva" | "scartata", correzione?: string) => {
    if (!scheda) return;
    avanti();
    void api.decidiScheda(scheda.id, stato, correzione).catch(() => {});
  };

  const scegliVersione = (tieniNuova: boolean) => {
    if (!scheda) return;
    avanti();
    void api.decidiAggiornamento(scheda.id, tieniNuova).catch(() => {});
  };

  // Una scheda già confermata, con una versione nuova accanto.
  const eAggiornamento = scheda?.stato === "attiva" && !!scheda.aggiornamento;

  return (
    <Modal
      open={aperta && scheda != null}
      onClose={onChiudi}
      stretta
      title="Rispondo così?"
      subtitle="L'ho imparato da come rispondi tu agli ospiti."
      media={
        <span className="grid size-10 shrink-0 place-items-center rounded-field bg-surface-muted text-ink-soft">
          <NotebookPen size={18} />
        </span>
      }
    >
      {scheda && eAggiornamento ? (
        <div key={`${scheda.id}:agg`} className="animate-rise">
          <p className="text-[15px] font-bold text-ink">
            Hai cominciato a rispondere diversamente a «
            {scheda.gruppo.toLowerCase()}»
          </p>
          <p className="mt-0.5 text-[12.5px] text-ink-faint">
            {nomeCasa(scheda)} · Nelle tue risposte più recenti dici un'altra
            cosa. Quale uso?
          </p>
          <p className="mt-3 text-[11.5px] font-bold uppercase tracking-[0.06em] text-ink-faint">
            Prima
          </p>
          <p className="mt-1 whitespace-pre-wrap rounded-card bg-surface-muted p-3 text-[13.5px] leading-relaxed text-ink-soft">
            {scheda.correzione ?? scheda.testo}
          </p>
          <p className="mt-3 text-[11.5px] font-bold uppercase tracking-[0.06em] text-ink-faint">
            Adesso
          </p>
          <p className="mt-1 whitespace-pre-wrap rounded-card bg-ok-soft p-3 text-[13.5px] leading-relaxed text-ink">
            {scheda.aggiornamento}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Pill
              size="md"
              variant="primary"
              onClick={() => scegliVersione(true)}
            >
              Usa la nuova
            </Pill>
            <Pill size="md" onClick={() => scegliVersione(false)}>
              Tieni quella di prima
            </Pill>
            <Pill size="md" variant="ghost" onClick={avanti}>
              Non ora
            </Pill>
          </div>
        </div>
      ) : scheda ? (
        <div key={scheda.id} className="animate-rise">
          <p className="text-[15px] font-bold text-ink">
            Quando mi chiedono «{scheda.gruppo.toLowerCase()}»
          </p>
          <p className="mt-0.5 text-[12.5px] text-ink-faint">
            L'ho scritta guardando {scheda.suQuante}{" "}
            {scheda.suQuante === 1 ? "tua risposta" : "tue risposte"}
            {scheda.lingua === "it" ? "" : `, in ${scheda.lingua}`}
            {" · "}
            {nomeCasa(scheda)}.
          </p>

          {inCorrezione ? (
            <>
              <textarea
                value={bozza}
                onChange={(e) => setBozza(e.target.value)}
                rows={6}
                aria-label="Come rispondi tu"
                className="mt-3 w-full rounded-field border border-line bg-surface p-3 text-[14px] leading-relaxed text-ink"
              />
              <div className="mt-3 flex flex-wrap gap-2">
                <Pill
                  size="md"
                  variant="primary"
                  onClick={() => decidi("attiva", bozza.trim())}
                  disabled={bozza.trim() === ""}
                >
                  Salva così
                </Pill>
                <Pill size="md" onClick={() => setInCorrezione(false)}>
                  Lascia stare
                </Pill>
              </div>
            </>
          ) : (
            <>
              <p className="mt-3 whitespace-pre-wrap rounded-card bg-surface-muted p-4 text-[14px] leading-relaxed text-ink">
                {scheda.testo}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Pill
                  size="md"
                  variant="primary"
                  onClick={() => decidi("attiva")}
                >
                  Sì, rispondi così
                </Pill>
                <Pill
                  size="md"
                  onClick={() => {
                    setInCorrezione(true);
                    setBozza(scheda.testo);
                  }}
                >
                  Correggi
                </Pill>
                <Pill size="md" variant="ghost" onClick={avanti}>
                  Non ora
                </Pill>
              </div>
            </>
          )}
        </div>
      ) : null}
    </Modal>
  );
}
